/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorC2,
	ResumeIndicatorC2Data,
} from "@/services/esus/indicator/esf/c2/getResumeIndicatorC2";
import {
	getResumeIndicatorC2ByHealthTeam,
	ResumeIndicatorC2ByHealthTeamData,
} from "@/services/esus/indicator/esf/c2/getResumeIndicatorC2ByHealthTeam";
import {
	DetailedCitizenScoresIndicatorC2Data,
	getDetailedCitizensScoresIndicatorC2,
} from "@/services/esus/indicator/esf/c2/getDetailedCitizensScoresIndicatorC2";
import {
	DetailedCitizenAcsVisitsIndicatorC2Data,
	getDetailedCitizensAcsVisitsIndicatorC2,
} from "@/services/esus/indicator/esf/c2/getDetailedCitizensAcsVisitsIndicatorC2";
import {
	DetailedCitizenVaccinationsIndicatorC2Data,
	getDetailedCitizensVaccinationsIndicatorC2,
} from "@/services/esus/indicator/esf/c2/getDetailedCitizensVaccinationsIndicatorC2";
import {
	DetailedCitizenAttendancesIndicatorC2Data,
	getDetailedCitizensAttendancesIndicatorC2,
} from "@/services/esus/indicator/esf/c2/getDetailedCitizensAttendancesIndicatorC2";

import { IndicatorEsfC2Resume } from "./IndicatorEsfC2Resume";
import { IndicatorEsfC2CitizensAttendances } from "./IndicatorEsfC2CitizensAttendances";
import { IndicatorEsfC2HealthTeams } from "./IndicatorEsfC2HealthTeams";
import { IndicatorEsfC2CitizensScores } from "./IndicatorEsfC2CitizensScores";
import { IndicatorEsfC2CitizensVaccinations } from "./IndicatorEsfC2CitizensVaccinations";
import { IndicatorEsfC2CitizensAcsVisits } from "./IndicatorEsfC2CitizensAcsVisits";

type IndicatorEsfC2Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEsfC2({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEsfC2Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorC2, setResumeIndicatorC2] = useState<ResumeIndicatorC2Data>(
		new ResumeIndicatorC2Data()
	);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorC2ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	const [pageNumberCitizensScores, setPageNumberCitizensScores] = useState(1);
	const [searchCitizensScore, setSearchCitizensScore] = useState("");
	const [isEvaluatedCitizensScore, setIsEvaluatedCitizensScore] = useState<boolean>();
	const debouncedSearchCitizensScore = useDebounce(searchCitizensScore, 2000);
	const [paginatedCitizensScores, setPaginatedCitizensScores] = useState(
		new Paginated<DetailedCitizenScoresIndicatorC2Data>()
	);
	const [isLoadingCitizensScores, setIsLoadingCitizensScores] = useState(false);

	const [pageNumberAttendances, setPageNumberAttendances] = useState(1);
	const [searchCitizensWithAttendance, setSearchCitizensWithAttendance] = useState("");
	const [isEvaluatedCitizensWithAttendance, setIsEvaluatedCitizensWithAttendance] =
		useState<boolean>();
	const debouncedSearchAttendance = useDebounce(searchCitizensWithAttendance, 2000);
	const [paginatedCitizensWithAttendances, setPaginatedCitizensWithAttendances] = useState(
		new Paginated<DetailedCitizenAttendancesIndicatorC2Data>()
	);
	const [isLoadingAttendances, setIsLoadingAttendances] = useState(false);

	const [pageNumberCitizensAcsVisits, setPageNumberCitizensAcsVisits] = useState(1);
	const [searchCitizensAcsVisit, setSearchCitizensAcsVisit] = useState("");
	const [isEvaluatedCitizensAcsVisit, setIsEvaluatedCitizensAcsVisit] = useState<boolean>();
	const debouncedSearchCitizensAcsVisit = useDebounce(searchCitizensAcsVisit, 2000);
	const [paginatedCitizensAcsVisits, setPaginatedCitizensAcsVisits] = useState(
		new Paginated<DetailedCitizenAcsVisitsIndicatorC2Data>()
	);
	const [isLoadingCitizensAcsVisits, setIsLoadingCitizensAcsVisits] = useState(false);

	const [pageNumberCitizensVaccinations, setPageNumberCitizensVaccinations] = useState(1);
	const [searchCitizensVaccination, setSearchCitizensVaccination] = useState("");
	const [isEvaluatedCitizensVaccination, setIsEvaluatedCitizensVaccination] = useState<boolean>();
	const debouncedSearchCitizensVaccination = useDebounce(searchCitizensVaccination, 2000);
	const [paginatedCitizensVaccinations, setPaginatedCitizensVaccinations] = useState(
		new Paginated<DetailedCitizenVaccinationsIndicatorC2Data>()
	);
	const [isLoadingCitizensVaccinations, setIsLoadingCitizensVaccinations] = useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorC2(),
			handleGetResumeIndicatorC2ByHealthTeam(),
			handleGetDetailedCitizensScoresIndicatorC2(),
			handleGetDetailedCitizensWithAttendancesIndicatorC2(),
			handleGetDetailedCitizensAcsVisitsIndicatorC2(),
			handleGetDetailedCitizensVaccinationsIndicatorC2(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorC2() {
		setResumeIndicatorC2(
			await getResumeIndicatorC2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorC2ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorC2ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorC2ByHealthTeam() {
		return (
			await getResumeIndicatorC2ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: 1,
				pageSize: paginatedHealthTeamsClassifications.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensScoresIndicatorC2(pageNumber?: number) {
		setIsLoadingCitizensScores(true);
		setPaginatedCitizensScores(
			await getDetailedCitizensScoresIndicatorC2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensScore,
				isEvaluated: isEvaluatedCitizensScore,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensScores(false);
	}

	async function handleGetAllDetailedCitizensScoresIndicatorC2() {
		return (
			await getDetailedCitizensScoresIndicatorC2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensScore,
				isEvaluated: isEvaluatedCitizensScore,
				pageNumber: 1,
				pageSize: paginatedCitizensScores.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensWithAttendancesIndicatorC2(pageNumber?: number) {
		setIsLoadingAttendances(true);
		setPaginatedCitizensWithAttendances(
			await getDetailedCitizensAttendancesIndicatorC2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensWithAttendance,
				isEvaluated: isEvaluatedCitizensWithAttendance,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingAttendances(false);
	}

	async function handleGetAllDetailedCitizensWithAttendancesIndicatorC2() {
		return (
			await getDetailedCitizensAttendancesIndicatorC2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensWithAttendance,
				isEvaluated: isEvaluatedCitizensWithAttendance,
				pageNumber: 1,
				pageSize: paginatedCitizensWithAttendances.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensAcsVisitsIndicatorC2(pageNumber?: number) {
		setIsLoadingCitizensAcsVisits(true);
		setPaginatedCitizensAcsVisits(
			await getDetailedCitizensAcsVisitsIndicatorC2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensAcsVisit,
				isEvaluated: isEvaluatedCitizensAcsVisit,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensAcsVisits(false);
	}

	async function handleGetAllDetailedCitizensAcsVisitsIndicatorC2() {
		return (
			await getDetailedCitizensAcsVisitsIndicatorC2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensAcsVisit,
				isEvaluated: isEvaluatedCitizensAcsVisit,
				pageNumber: 1,
				pageSize: paginatedCitizensAcsVisits.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensVaccinationsIndicatorC2(pageNumber?: number) {
		setIsLoadingCitizensVaccinations(true);
		setPaginatedCitizensVaccinations(
			await getDetailedCitizensVaccinationsIndicatorC2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensVaccination,
				isEvaluated: isEvaluatedCitizensVaccination,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensVaccinations(false);
	}

	async function handleGetAllDetailedCitizensVaccinationsIndicatorC2() {
		return (
			await getDetailedCitizensVaccinationsIndicatorC2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensVaccination,
				isEvaluated: isEvaluatedCitizensVaccination,
				pageNumber: 1,
				pageSize: paginatedCitizensVaccinations.totalRecords,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorC2ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorC2ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensScoresIndicatorC2(pageNumberCitizensScores);
	}, [pageNumberCitizensScores]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensScores === 1) {
				handleGetDetailedCitizensScoresIndicatorC2(pageNumberCitizensScores);
			} else {
				setPageNumberCitizensScores(1);
			}
		}
	}, [debouncedSearchCitizensScore, isEvaluatedCitizensScore]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensWithAttendancesIndicatorC2(pageNumberAttendances);
	}, [pageNumberAttendances]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberAttendances === 1) {
				handleGetDetailedCitizensWithAttendancesIndicatorC2(pageNumberAttendances);
			} else {
				setPageNumberAttendances(1);
			}
		}
	}, [debouncedSearchAttendance, isEvaluatedCitizensWithAttendance]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensAcsVisitsIndicatorC2(pageNumberCitizensAcsVisits);
	}, [pageNumberCitizensAcsVisits]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensAcsVisits === 1) {
				handleGetDetailedCitizensAcsVisitsIndicatorC2(pageNumberCitizensAcsVisits);
			} else {
				setPageNumberCitizensAcsVisits(1);
			}
		}
	}, [debouncedSearchCitizensAcsVisit, isEvaluatedCitizensAcsVisit]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensVaccinationsIndicatorC2(pageNumberCitizensVaccinations);
	}, [pageNumberCitizensVaccinations]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensVaccinations === 1) {
				handleGetDetailedCitizensVaccinationsIndicatorC2(pageNumberCitizensVaccinations);
			} else {
				setPageNumberCitizensVaccinations(1);
			}
		}
	}, [debouncedSearchCitizensVaccination, isEvaluatedCitizensVaccination]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("C2 - Cuidado no Desenvolvimento Infantil");
	}, [setTitle]);

	return (
		<>
			<IndicatorEsfC2Resume resume={resumeIndicatorC2} />
			<IndicatorEsfC2HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorC2ByHealthTeam={
					handleGetAllResumeIndicatorC2ByHealthTeam
				}
			/>
			<IndicatorEsfC2CitizensScores
				paginatedCitizensScores={paginatedCitizensScores}
				isLoading={isLoadingCitizensScores}
				search={searchCitizensScore}
				setSearch={setSearchCitizensScore}
				isEvaluated={isEvaluatedCitizensScore}
				setIsEvaluated={setIsEvaluatedCitizensScore}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensScores}
				setPageNumber={setPageNumberCitizensScores}
				handleGetAllDetailedCitizensScoresIndicatorC2={
					handleGetAllDetailedCitizensScoresIndicatorC2
				}
			/>
			<IndicatorEsfC2CitizensAttendances
				paginatedAttendances={paginatedCitizensWithAttendances}
				isLoading={isLoadingAttendances}
				search={searchCitizensWithAttendance}
				setSearch={setSearchCitizensWithAttendance}
				isEvaluated={isEvaluatedCitizensWithAttendance}
				setIsEvaluated={setIsEvaluatedCitizensWithAttendance}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberAttendances}
				setPageNumber={setPageNumberAttendances}
				handleGetAllDetailedIndicatorC2={
					handleGetAllDetailedCitizensWithAttendancesIndicatorC2
				}
			/>
			<IndicatorEsfC2CitizensAcsVisits
				paginatedCitizensAcsVisits={paginatedCitizensAcsVisits}
				isLoading={isLoadingCitizensAcsVisits}
				search={searchCitizensAcsVisit}
				setSearch={setSearchCitizensAcsVisit}
				isEvaluated={isEvaluatedCitizensAcsVisit}
				setIsEvaluated={setIsEvaluatedCitizensAcsVisit}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensAcsVisits}
				setPageNumber={setPageNumberCitizensAcsVisits}
				handleGetAllDetailedCitizensAcsVisitsIndicatorC2={
					handleGetAllDetailedCitizensAcsVisitsIndicatorC2
				}
			/>
			<IndicatorEsfC2CitizensVaccinations
				paginatedCitizensVaccinations={paginatedCitizensVaccinations}
				isLoading={isLoadingCitizensVaccinations}
				search={searchCitizensVaccination}
				setSearch={setSearchCitizensVaccination}
				isEvaluated={isEvaluatedCitizensVaccination}
				setIsEvaluated={setIsEvaluatedCitizensVaccination}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensVaccinations}
				setPageNumber={setPageNumberCitizensVaccinations}
				handleGetAllDetailedCitizensVaccinationsIndicatorC2={
					handleGetAllDetailedCitizensVaccinationsIndicatorC2
				}
			/>
		</>
	);
}
